import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Collapsible from '../Collapsible';
import { markdownLinkComponents, practiceUrlTransform } from '../../utils/practiceLinks';
import { parseWeekBody } from '../../utils/excellenceContent';

function Md({ children, onStartPractice }) {
  if (!children) return null;
  return (
    <div className="markdown-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={practiceUrlTransform} components={markdownLinkComponents(onStartPractice)}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

// A single worked example: the scenario/question is always visible once
// the surrounding "Examples" section is open, but the correct answer and
// reasoning stay hidden — so a student judges the example themselves
// first — until they deliberately click to reveal it.
function ExampleCard({ example, onStartPractice }) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="example-card">
      {example.header && (
        <div className="example-card-header">
          <span className="example-card-title">{example.header}</span>
          {example.meta && <span className="example-card-meta">{example.meta}</span>}
        </div>
      )}
      <Md onStartPractice={onStartPractice}>{example.question}</Md>
      {example.answer && (
        revealed ? (
          <div className="example-answer-reveal">
            <Md onStartPractice={onStartPractice}>{example.answer}</Md>
            <button type="button" className="example-answer-toggle example-answer-toggle-hide" onClick={() => setRevealed(false)}>
              Hide answer
            </button>
          </div>
        ) : (
          <button type="button" className="example-answer-toggle" onClick={() => setRevealed(true)}>
            Show correct answer &amp; explanation
          </button>
        )
      )}
    </div>
  );
}

function ExcellenceWeekContent({ body, onStartPractice }) {
  const { intro, sections } = parseWeekBody(body);

  if (!intro && sections.length === 0) {
    return <p className="content-empty">Content for this week hasn't been added yet.</p>;
  }

  return (
    <div className="excellence-week-content">
      {intro && <Md onStartPractice={onStartPractice}>{intro}</Md>}

      {sections.map((section, i) => {
        if (section.kind === 'theory') {
          // A week with no ### sub-topics at all (e.g. Week 1's single
          // intro-to-UCAT block) parses as one anonymous subsection —
          // nesting a second collapsible for it would just repeat this
          // same title, so render its body directly instead.
          const hasNamedSubsections = section.subsections.some((sub) => sub.title);
          return (
            <Collapsible key={i} title={section.title}>
              {hasNamedSubsections ? (
                section.subsections.map((sub, j) => (
                  <Collapsible key={j} title={sub.title} nested>
                    <Md onStartPractice={onStartPractice}>{sub.body}</Md>
                  </Collapsible>
                ))
              ) : (
                <Md onStartPractice={onStartPractice}>{section.subsections[0]?.body}</Md>
              )}
            </Collapsible>
          );
        }
        if (section.kind === 'examples') {
          return (
            <Collapsible key={i} title={section.title} badge={`${section.examples.length}`}>
              <div className="example-card-list">
                {section.examples.map((ex, j) => (
                  <ExampleCard key={j} example={ex} onStartPractice={onStartPractice} />
                ))}
              </div>
            </Collapsible>
          );
        }
        return (
          <Collapsible key={i} title={section.title || 'More'}>
            <Md onStartPractice={onStartPractice}>{section.body}</Md>
          </Collapsible>
        );
      })}
    </div>
  );
}

export default ExcellenceWeekContent;
